import React, {useEffect, useState} from 'react';
import {inject, observer} from 'mobx-react';
import {CourseAccessGate} from '../../../modules/CourseAccessGate';
import {Course} from '../../../data/models/Course/Course';
import {useNavigate, useParams} from 'react-router-dom';
import {
  Lesson,
  isLessonFinalAssessment,
} from '../../../data/models/Course/Lesson';
import {Folder} from '../../../data/models/Course/Folder';
import {LessonEditor} from '../../LessonEditor';
import CourseLessonEditorStore from '../store/CourseLessonEditorStore';

interface Props {
  courseLessonEditorStore?: CourseLessonEditorStore;
}

const CourseLessonEditor = inject('courseLessonEditorStore')(
  observer(({courseLessonEditorStore}: Props) => {
    const [course, setCourse] = useState<Course>();
    const [lesson, setLesson] = useState<Lesson>();
    const [folder, setFolder] = useState<Folder>();

    const navigate = useNavigate();
    const {courseId, organizationId, folderId, lessonId} = useParams();

    useEffect(() => {
      if (organizationId && courseId && folderId && lessonId) {
        courseLessonEditorStore!
          .getLesson(organizationId, lessonId)
          .then(lesson => {
            if (lesson?.inProgress) {
              navigate(-1);
            } else {
              setLesson(lesson);
            }
          });
        courseLessonEditorStore
          ?.getCourse(organizationId, courseId)
          .then(setCourse);
        courseLessonEditorStore
          ?.getLessonFolder(organizationId, folderId)
          .then(setFolder);
      }
    }, [courseId, folderId, lessonId]);

    const getBreadcrumbs = () => [
      {
        title: 'Courses',
        link: `/manage/${organizationId}/courses`,
      },
      {
        title: course!.title,
        link: `/editor/${organizationId}/courses/${courseId}/folders`,
      },
      {
        title: folder!.name,
        link: `/editor/${organizationId}/courses/${courseId}/folders/${folderId}/lessons`,
      },
      {
        title: lesson!.title,
        link: `#`,
      },
    ];

    const onBack = () => {
      navigate(
        `/editor/${organizationId}/courses/${courseId}/folders/${folderId}/lessons`,
        {replace: true},
      );
    };

    const onPreview = () => {
      navigate(
        `/preview/${organizationId}/course/${courseId}/lesson/${lessonId}`,
      );
    };

    const onPublish = () => {
      navigate(`/preview/${organizationId}/course/${courseId}`);
    };

    const exportAsScorm = () => {
      if (organizationId && course && courseId) {
        courseLessonEditorStore!.exportAsScorm(
          organizationId,
          courseId,
          course.title,
        );
      }
    };

    return course && lesson && folder ? (
      <CourseAccessGate>
        <LessonEditor
          breadcrumbs={getBreadcrumbs()}
          isGradable={lesson.isGradable}
          onBack={onBack}
          onPreview={onPreview}
          onPublish={onPublish}
          exportAsScorm={exportAsScorm}
          lessonTitle={lesson.title}
          currentLessonId={lesson.id}
          isFinalAssessment={isLessonFinalAssessment(lesson)}
        />
      </CourseAccessGate>
    ) : null;
  }),
);

export default CourseLessonEditor;
